import React, { useState } from "react";
import { FaChartBar, FaChartPie } from "react-icons/fa";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useChartTheme } from "./ChartTheme";
import DashboardCard from "./DashboardCard";
import CustomNoData from "../../../components/CustomNoData";

const CHART_COLORS = ["#2563EB", "#00B4D8", "#008000", "#F59E0B", "#EF4444", "#8B5CF6","#E76F51","#0D1B2A","#7CB342","#F3CFFF","#33001b"];

const DistributionChart = ({ data = [] }) => {
  const chartTheme = useChartTheme();
  const [chartType, setChartType] = useState("bar");

  const chartData = data.map((item) => ({
    ...item,
    value: Number(item.value) || 0,
    win: item.win || 0
  }));

  const formatTooltipValue = (value, name, props) => [
    `${props.payload?.displayValue ?? value} / ${props.payload?.percent ?? 0}%`,
    props.payload?.label ?? name,
  ];

  return (
    <DashboardCard className="p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-customInk dark:text-customHeading">Bet Distribution by Mix</h2>
        <div className="flex h-8 items-center rounded-md border border-customAccentBlue/20 bg-customSlate p-0.5 dark:border-customBorder dark:bg-customSurfaceMuted">
          <button
            type="button"
            title="Bar chart"
            aria-label="Show bar chart"
            onClick={() => setChartType("bar")}
            className={`flex h-7 w-8 items-center justify-center rounded text-sm transition ${
              chartType === "bar"
                ? "bg-customAccentBlue text-white shadow-sm"
                : "text-customSecondaryText hover:text-customInk dark:text-customMuted dark:hover:text-customHeading"
            }`}
          >
            <FaChartBar />
          </button>
          <button
            type="button"
            title="Pie chart"
            aria-label="Show pie chart"
            onClick={() => setChartType("pie")}
            className={`flex h-7 w-8 items-center justify-center rounded text-sm transition ${
              chartType === "pie"
                ? "bg-customAccentBlue text-white shadow-sm"
                : "text-customSecondaryText hover:text-customInk dark:text-customMuted dark:hover:text-customHeading"
            }`}
          >
            <FaChartPie />
          </button>
        </div>
      </div>
      {data.length === 0 ? (
        <div className="h-[200px]">
          <CustomNoData title="No mix data" description="No mix distribution data found for the selected date range." py={16} />
        </div>
      ) : chartType === "pie" ? (
        <>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={32} outerRadius={82} paddingAngle={2}>
                  {chartData.map((item, index) => (
                    <Cell key={item.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={formatTooltipValue}
                  contentStyle={{
                    backgroundColor: chartTheme.tooltipBackground,
                    borderColor: chartTheme.grid,
                    color: chartTheme.tooltipText,
                    borderRadius: 8,
                  }}
                  itemStyle={{ color: chartTheme.tooltipText }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
            {chartData.map((item, index) => (
              <div key={item.label} className="flex min-w-0 items-center gap-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                <span className="shrink-0 font-medium text-customPrimaryText dark:text-customHeading">{item.label}</span>
                <span className="min-w-0 truncate text-customSecondaryText dark:text-customMuted">
                  {item.displayValue} / <span className="text-blue">{item.percent}%</span>
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
         <div className="grid grid-cols-[44px_1fr_0.45fr] text-xs pb-2">
              <React.Fragment>
                <span className="font-medium text-customSecondaryText dark:text-customHeading">Type</span>
                <div className="h-5 rounded-sm max-w-[280px] text-center text-customSecondaryText font-medium">
                  Percentage
                </div>
                <span className="pl-3 text-[11px] font-medium text-customSecondaryText dark:text-customMuted">
                  Bet Amt / <span>%</span>
                  {/* {item.displayValue} / <span className="text-customAccentBlue">+{item.win}</span> */}
                </span>
              </React.Fragment>
          </div>
          <div className="grid grid-cols-[44px_1fr_0.45fr] gap-y-3 text-xs pt-2">
            {data.map((item) => (
              <React.Fragment key={item.label}>
                <span className="font-medium text-customPrimaryText dark:text-customHeading">{item.label}</span>
                <div className="h-5 rounded-sm bg-customSlate dark:bg-customSurfaceMuted max-w-[280px]">
                  <div className="h-full rounded-sm bg-customAccentBlue" style={{ width: `${item.percent * 2.6}%` }} />
                </div>
                <span className="pl-3 text-[11px] font-medium text-customPrimaryText dark:text-customMuted">
                  {item.displayValue} / <span className="text-blue">{item.percent}%</span>
                  {/* {item.displayValue} / <span className="text-customAccentBlue">+{item.win}</span> */}
                </span>
              </React.Fragment>
            ))}
          </div>
          {/* <div className="mt-2 h-10">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[{ value: 0 }, { value: 10 }, { value: 20 }, { value: 30 }, { value: 40 }, { value: 50 }]}>
                <CartesianGrid vertical={false} stroke={chartTheme.grid} />
                <XAxis dataKey="value" tickFormatter={(value) => `${value}M`} tick={{ fill: chartTheme.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Bar dataKey="hidden" fill="transparent" />
              </BarChart>
            </ResponsiveContainer>
          </div> */}
          <p className="text-center text-[11px] text-customSecondaryText dark:text-customMuted">Amount (M Ks)</p>
        </>
      )}
    </DashboardCard>
  );
};

export default DistributionChart;
